import React, { useState, useEffect, useCallback } from 'react';
import { useWindows } from '../../contexts/WindowContext';
import { useContextMenu } from '../../contexts/ContextMenuContext';
import { StartButton } from './StartButton';
import { StartMenu } from './StartMenu';
import { TaskbarItem } from './TaskbarItem';
import { SystemTray } from './SystemTray';
import type { ContextMenuItem } from '../../types';
import styles from './Taskbar.module.css';

export const Taskbar: React.FC = () => {
  const { windows, arrangeWindows, minimizeWindow } = useWindows();
  const { showContextMenu } = useContextMenu();
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);

  // Taskbar context menu items
  const getTaskbarMenuItems = useCallback((): ContextMenuItem[] => [
    {
      id: 'cascade',
      label: 'Cascade Windows',
      icon: 'cascade',
      action: () => arrangeWindows('cascade'),
    },
    {
      id: 'tile-h',
      label: 'Tile Windows Horizontally',
      icon: 'tile-horizontal',
      action: () => arrangeWindows('tile-horizontal'),
    },
    {
      id: 'tile-v',
      label: 'Tile Windows Vertically',
      icon: 'tile-vertical',
      action: () => arrangeWindows('tile-vertical'),
    },
    { id: 'divider-1', label: '', divider: true },
    {
      id: 'minimize-all',
      label: 'Show Desktop',
      action: () => {
        // Minimize all windows
        windows.forEach(w => {
          if (w.state !== 'minimized') {
            minimizeWindow(w.id);
          }
        });
      },
      disabled: windows.length === 0,
    },
  ], [arrangeWindows, minimizeWindow, windows]);

  const handleTaskbarContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    showContextMenu(
      { x: e.clientX, y: e.clientY },
      getTaskbarMenuItems(),
      'taskbar'
    );
  }, [showContextMenu, getTaskbarMenuItems]);

  // Close start menu when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.startArea}`) && !target.closest('[data-start-menu]')) {
        setIsStartMenuOpen(false);
      }
    };

    if (isStartMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isStartMenuOpen]);

  // Close start menu on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsStartMenuOpen(false);
      }
    };

    if (isStartMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isStartMenuOpen]);

  const toggleStartMenu = () => {
    setIsStartMenuOpen(prev => !prev);
  };

  const closeStartMenu = () => {
    setIsStartMenuOpen(false);
  };

  return (
    <>
      {isStartMenuOpen && <StartMenu onClose={closeStartMenu} />}
      <div
        className={styles.taskbar}
        role="navigation"
        aria-label="Taskbar"
        onContextMenu={handleTaskbarContextMenu}
      >
        <div className={styles.startArea}>
          <StartButton isOpen={isStartMenuOpen} onClick={toggleStartMenu} />
        </div>

        <div className={styles.quickLaunch}>
          {/* Quick launch icons could go here */}
        </div>

        <div className={styles.windowsArea}>
          {windows.map(window => (
            <TaskbarItem key={window.id} window={window} />
          ))}
        </div>

        <SystemTray />
      </div>
    </>
  );
};

export default Taskbar;
